// Copyright (c) 2010-2025 IQGeo Group Plc. Use subject to conditions at $MYWORLD_HOME/Docs/legal.txt
// Adapted from Leaflet (https://github.com/Leaflet/Leaflet)
import { Localisable } from './localisation';

function falseFn() {
    return false;
}

/**
 * Trims the whitespace from the beginning and end of the string.
 * @param {string} str
 * @returns {string}
 * @private
 */
function trim(str) {
    return str.trim ? str.trim() : str.replace(/^\s+|\s+$/g, '');
}

/**
 * Trims and splits the string on whitespace and returns the array of parts.
 * @param {string} str
 * @returns {string[]}
 * @private
 */
function splitWords(str) {
    return trim(str).split(/\s+/);
}

/**
 * Merges given objects (mixins) into the given class
 * @param  {Class} targetClass
 * @param  {...Record<PropertyKey, any>} mixins
 * @example
 *  const MyMixin = {
 *      foo() { return 123+this.bar; },
 *      bar: 5
 *  };
 *
 *  class MyClass  {
 *  }
 *  include(MyClass, MyMixin)
 *
 *  const a = new MyClass();
 *  a.foo(); // 128
 */
export function include(targetClass, ...mixins) {
    const proto = targetClass.prototype;
    //ensure items already in prototype override what comes from mixins
    const propDescriptors = [...mixins, proto].reduce((acc, mixin) => {
        if (typeof mixin !== 'object') {
            console.error(`${this.name} include() call - invalid mixin object:`, mixin);
            return acc;
        } else return Object.assign(acc, Object.getOwnPropertyDescriptors(mixin));
    }, {});
    Object.defineProperties(proto, propDescriptors);
}

/**
 * @classdesc Base class that provides easier handling of options and mixins as well as localisation methods
 */
export class MywClass extends Localisable {
    static {
        this.prototype.messageGroup = 'MywClass';
    }

    /**
     * Merges given objects (mixins) into the class
     * @param  {...Record<PropertyKey, any>} mixins
     * @example
     *  const MyMixin = {
     *      foo() { return 123+this.bar; },
     *      bar: 5
     *  };
     *
     *  class MyClass extends MywClass {
     *      static {
     *          this.includes(MyMixin)
     *      }
     *  }
     *
     *  const a = new MyClass();
     *  a.foo(); // 128
     */
    static include(...mixins) {
        include(this, ...mixins);
    }

    /**
     * Merges given options with options from parent class
     * @param {object} options
     * @example
     * class MyClass extends MywClass {
     *     static {
     *         this.mergeOptions({
     *             myOption1: 'foo',
     *             myOption2: 'bar'
     *         });
     *     }
     * }
     *
     * class MyChildClass extends MyClass {
     *     static {
     *         this.mergeOptions({
     *             myOption1: 'baz',
     *             myOption3: 5
     *         });
     *     }
     * }
     *
     * let a = new MyChildClass();
     * a.options.myOption1; // 'baz'
     * a.options.myOption2; // 'bar'
     * a.options.myOption3; // 5
     */
    static mergeOptions(options) {
        this.prototype.options = Object.assign({}, this.prototype.options, options);
    }

    constructor() {
        super();

        /**
         * @type {string}
         */
        this.messageGroup;
    }

    /**
     * Sets the options property with the result of merging the given options with the default set on the class
     * @param {object} options
     * @returns {object} the merged options
     * @example
     * class MyClass extends myw.MywClass {
     *     static {
     *         this.mergeOptions({
     *             foo: 'bar',
     *             bla: 5
     *         });
     *     }
     *
     *     constructor (options) {
     *         super();
     *         this.setOptions(options);
     *     }
     * }
     *
     * let c = new MyClass({bla: 10}); // {foo: 'bar', bla: 10}
     */
    setOptions(options) {
        if (this.options?.v) options = this.upgradeOptionsTo(this.options.v, options);
        this.options = Object.assign({}, this.options, options);
        return this.options;
    }

    /**
     * Updates the given options from a previous schema to the target schema version
     * @param {number} targetVersion
     * @param {object} options
     * @returns {object} upgraded options
     */
    upgradeOptionsTo(targetVersion, options) {
        for (let v = options.v || 1; v < targetVersion; v++) {
            const method = this[`upgradeOptionsToV${v + 1}`];
            if (method) options = method.call(this, options);
            else console.log(`Missing method upgradeOptionsToV${v + 1} on `, this.constructor.name);
        }
        return options;
    }

    /**
     * Adds a listener function (`fn`) to a particular event type of the object.
     * You can optionally specify the context of the listener (object the this keyword will point to).
     * You can also pass several space-separated types (e.g. `'click dblclick'`).
     * @overload
     * @param {string} types
     * @param {Function} fn
     * @param {Record<string, any>} [context]
     * @returns {MywClass}
     */ /**
     * Adds a set of type/listener pairs, e.g. `{click: onClick, mousemove: onMouseMove}`
     * @overload
     * @param {Record<string, any>} types
     * @returns {MywClass}
     */ /**
     * @param {string | object} types
     * @param {Function} [fn]
     * @param {Record<string, any>} [context]
     * @returns {MywClass}
     */
    on(types, fn, context) {
        // types can be a map of types/handlers
        if (typeof types === 'object') {
            for (const [type, val] of Object.entries(types)) {
                // we don't process space-separated events here for performance;
                // it's a hot path since Layer uses the on(obj) syntax
                this._on(type, val, fn);
            }
        } else {
            // types can be a string of space-separated words
            types = splitWords(types);

            for (let i = 0, len = types.length; i < len; i++) {
                this._on(types[i], fn, context);
            }
        }

        return this;
    }

    /**
     * Removes a previously added listener function. If no function is specified, it will remove all
     * the listeners of that particular event from the object. Note that if you passed a custom
     * context to `on`, you must pass the same context to `off` in order to remove the listener.
     * @overload
     * @param {string} types
     * @param {Function} [fn]
     * @param {Record<string, any>} [context]
     * @returns {MywClass}
     */ /**
     * Removes a set of type/listener pairs.
     * @overload
     * @param {Record<string, any>} types
     * @returns {MywClass}
     */ /**
     * Removes all listeners to all events on the object.
     * @overload
     * @returns {MywClass}
     */ /**
     * @param {string | object} [types]
     * @param {Function} [fn]
     * @param {Record<string, any>} [context]
     * @returns {MywClass}
     */
    off(types, fn, context) {
        if (!types) {
            // clear all listeners if called without arguments
            delete this._events;
        } else if (typeof types === 'object') {
            for (const [type, val] of Object.entries(types)) {
                this._off(type, val, fn);
            }
        } else {
            types = splitWords(types);

            for (let i = 0, len = types.length; i < len; i++) {
                this._off(types[i], fn, context);
            }
        }

        return this;
    }

    /**
     * @param {string} type
     * @param {Function} fn
     * @param {Record<string, any>} context
     * @private
     */
    _on(type, fn, context) {
        this._events = this._events || {};

        /* get/init listeners for type */
        let typeListeners = this._events[type];
        if (!typeListeners) {
            typeListeners = [];
            this._events[type] = typeListeners;
        }

        if (context === this) {
            // Less memory footprint.
            context = undefined;
        }
        const newListener = { fn: fn, ctx: context },
            listeners = typeListeners;

        // check if fn already there
        for (let i = 0, len = listeners.length; i < len; i++) {
            if (listeners[i].fn === fn && listeners[i].ctx === context) {
                return;
            }
        }

        listeners.push(newListener);
    }

    /**
     * @param {string} type
     * @param {Function} fn
     * @param {Record<string, any>} context
     * @private
     */
    _off(type, fn, context) {
        let listeners, i, len;

        if (!this._events) {
            return;
        }

        listeners = this._events[type];

        if (!listeners) {
            return;
        }

        if (!fn) {
            // Set all removed listeners to noop so they are not called if remove happens in fire
            for (i = 0, len = listeners.length; i < len; i++) {
                listeners[i].fn = falseFn;
            }
            // clear all listeners for a type if function isn't specified
            delete this._events[type];
            return;
        }

        if (context === this) {
            context = undefined;
        }

        if (listeners) {
            // find fn and remove it
            for (i = 0, len = listeners.length; i < len; i++) {
                const l = listeners[i];
                if (l.ctx !== context) {
                    continue;
                }
                if (l.fn === fn) {
                    // set the removed listener to noop so that's not called if remove happens in fire
                    l.fn = falseFn;

                    if (this._firingCount) {
                        /* copy array in case events are being fired */
                        this._events[type] = listeners = listeners.slice();
                    }
                    listeners.splice(i, 1);

                    return;
                }
            }
        }
    }

    /**
     * Fires an event of the specified type. You can optionally provide an data
     * object — the first argument of the listener function will contain its
     * properties.
     * @param {string} type
     * @param {Record<string, any>} [data]
     * @returns {MywClass}
     */
    fire(type, data) {
        if (!this.listens(type)) {
            return this;
        }

        const event = Object.assign({}, data, { type: type, target: this });

        if (this._events) {
            const listeners = this._events[type];

            if (listeners) {
                this._firingCount = this._firingCount + 1 || 1;
                for (let i = 0, len = listeners.length; i < len; i++) {
                    const l = listeners[i];
                    l.fn.call(l.ctx || this, event);
                }

                this._firingCount--;
            }
        }

        return this;
    }

    /**
     * Returns `true` if a particular event type has any listeners attached to it.
     * @param {string} type
     * @returns {boolean}
     */
    listens(type) {
        const listeners = this._events && this._events[type];
        if (listeners?.length) {
            return true;
        }

        return false;
    }

    /**
     * Behaves as {@link MywClass.on}, except the listener will only get fired once and then removed.
     * @overload
     * @param {string} types
     * @param {Function} fn
     * @param {Record<string, any>} [context]
     * @returns {MywClass}
     */ /**
     * Adds a set of type/listener pairs, e.g. `{click: onClick, mousemove: onMouseMove}`
     * @overload
     * @param {Record<string, any>} types
     * @returns {MywClass}
     */ /**
     * @param {string | object} types
     * @param {Function} [fn]
     * @param {Record<string, any>} [context]
     * @returns {MywClass}
     */
    once(types, fn, context) {
        if (typeof types === 'object') {
            for (const [type, val] of Object.entries(types)) {
                this.once(type, val, fn);
            }
            return this;
        }

        const handler = () => {
            this.off(types, fn, context).off(types, handler, context);
        };

        // add a listener that's executed once and removed after that
        return this.on(types, fn, context).on(types, handler, context);
    }
}

export default MywClass;
